import React, { useRef, useState } from 'react';
import AutoplayCarousel from '../components/carousel/AutoplayCarousel';
import Footer from '../components/Footer';
import landingImage from '../images/landingImage.webp';
import emailIcon from '../icons/waitlist_form/email.svg';
import phoneIcon from '../icons/waitlist_form/phone.svg';
import companyIcon from '../icons/waitlist_form/companyName.svg';
import firstNameIcon from '../icons/waitlist_form/firstname.svg';
import waitlistCongratsAni from '../images/waitlistCongratAni.gif';
import waitlistCompleteAnim from '../images/waitlistCompleteAnim.svg';
import emailjs from '@emailjs/browser';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CustomDemoButton from '../components/Buttons/CustomDemoButton';
import AnimatedSection from '../Animation/AnimatiedSection';
import ZoomInAnimatedSection from '../Animation/ZoomInAnimatedSection';

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [focusedInput, setFocusedInput] = useState('');
  const [waitlistCondition, setWaitlistCondition] = useState('');
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone_number: '',
    role: '',
  });

  const [formErrors, setFormErrors] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone_number: '',
    role: '',
  });

  const formRef = useRef(null);
  const userData = useSelector((state) => state?.authReducer?.userData);
  const role = userData?.role;

  const tech = t('home.tech', { returnObjects: true });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    validateField(name, value);
  };

  const validateField = (name, value) => {
    setFormErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      switch (name) {
        case 'first_name':
          newErrors.first_name =
            value.trim() === '' ? t('validation.required.firstName') : '';
          break;
        case 'last_name':
          newErrors.last_name =
            value.trim() === '' ? t('validation.required.lastName') : '';
          break;
        case 'company_name':
          newErrors.company_name =
            value.trim() === '' ? t('validation.required.companyName') : '';
          break;
        case 'email':
          newErrors.email =
            value.trim() === ''
              ? t('validation.required.email')
              : !/\S+@\S+\.\S+/.test(value)
                ? t('validation.valid.email')
                : '';
          break;
        case 'phone_number':
          newErrors.phone_number =
            value.trim() === ''
              ? t('validation.required.phoneNumber')
              : !/^\d+$/.test(value)
                ? t('validation.valid.phoneNumber')
                : '';
          break;
        case 'role':
          newErrors.role =
            value.trim() === '' ? t('validation.required.role') : '';
          break;
        default:
          break;
      }

      return newErrors;
    });
  };

  const scrollToForm = () => {
    // if (formRef.current) {
    //   formRef.current.scrollIntoView({ behavior: 'smooth' });
    // }

    window.open('https://cal.com/ssjaiswal', '_blank');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let valid = true;
    Object.keys(formData).forEach((field) => {
      if (formData[field].trim() === '') {
        validateField(field, formData[field]);
        valid = false;
      }
    });

    if (valid) {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        metadata: {
          company_name: formData.company_name,
          phone_number: formData.phone_number,
          role: formData.role,
        },
      };
      setProcessing(true);
      axios
        .post('https://backend.makersharks.ai/user-waitlist', payload, {
          headers: {
            'X-Api-Key': 'bc631e4efc8a7e9bd6ee37d9ad0ff3c8',
            'Content-Type': 'application/json',
          },
        })
        .then((response) => {
          setProcessing(false);
          sendEmail();
          // setWaitlistCondition('schedule');
          handleScheduleDemo();

          setFormData({
            first_name: '',
            last_name: '',
            company_name: '',
            email: '',
            phone_number: '',
            role: '',
          });
        })
        .catch((error) => {
          console.log('Error: ', error);
        });
    } else {
      console.log('Validation errors:', formErrors);
    }
  };

  const handleScheduleDemo = () => {
    setWaitlistCondition('congrats');

    setTimeout(() => {
      setWaitlistCondition('complete');
    }, 1100);
  };

  const sendEmail = () => {
    emailjs
      .send(
        'Makersharks_waitlist',
        'template_y94jvth',
        {
          to_name: formData.first_name,
          to_email: formData.email,
        },
        {
          publicKey: '_2Z_97_GgLa7_oicl',
        },
      )
      .then(
        () => {
          console.log('SUCCESS!');
        },
        (error) => {
          console.log('FAILED...', error.text);
        },
      );
  };

  return (
    <div className="homePageScroller bg-white min-h-screen font-roboto relative overflow-y-auto">
      {/* Hero Section */}
      <AnimatedSection>
        <div className="relative overflow-hidden bg-[#eae1f9] rounded-b-[40px] lg:rounded-0 lg:bg-white px-4 pt-12 pb-8 lg:py-12 lg:px-12 lg:pl-16">
          {/* Wavy Background */}
          <div className="hidden lg:block lg:absolute lg:inset-x-0 lg:-bottom-2 lg:z-0">
            <img src="/wavyDown.svg" alt="Wavy Background" className="w-full" />
          </div>

          <div className="relative z-10 lg:flex lg:flex-row lg:items-center">
            {/* Text Section */}
            <div className="w-full lg:max-w-full">
              <p className="w-full font-extrabold text-black leading-tight text-4xl sm:text-[56px]">
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      'linear-gradient(350deg, #6C3BB7 0%, #8E5DDA 30%, #7636D9 50%)',
                  }}
                >
                  {t('home.section1.h1')}
                </span>
                <br />
                {t('home.section1.h2')} <br />
              </p>
              <p className="mt-6 text-base text-gray-600">
                {t('home.section1.desc1')}
              </p>
              {role ? (
                <div className="flex flex-row items-center space-x-5 mt-8">
                  <button
                    className="px-8 py-3 text-white text-xl font-semibold rounded-xl shadow-lg transition-transform transform hover:scale-105"
                    style={{
                      background:
                        'linear-gradient(126deg, #6C3BB7 0%, #A689D2 28%, #7636D9 60%)',
                      backgroundBlendMode: 'overlay',
                    }}
                    onClick={() => {
                      navigate(`/${role}/dashboard`);
                    }}
                  >
                    Dashboard
                  </button>
                </div>
              ) : (
                <div className="flex flex-row items-center space-x-5 mt-8">
                  {/* <button
                  className="px-8 py-3 text-white text-xl font-semibold rounded-md shadow-lg transition-transform transform hover:scale-105"
                  style={{
                    background:
                      'linear-gradient(126deg, #6C3BB7 0%, #A689D2 28%, #7636D9 60%)',
                    backgroundBlendMode: 'overlay',
                  }}
                  onClick={scrollToForm}
                >
                  {t('common.buttons.joinWaitlist')}
                </button> */}
                  <CustomDemoButton
                    key={'Book a Demo'}
                    to={'https://cal.com/ssjaiswal'}
                    type="homePage1"
                    size="text-xl px-8 py-3"
                  >
                    Book a demo
                  </CustomDemoButton>
                  {/* <span className="text-lg font-medium text-gray-600 flex-shrink-0">
                  Or
                </span>
                <button
                  className="px-8 py-3 text-white text-xl font-semibold rounded-xl shadow-lg transition-transform transform hover:scale-105"
                  style={{
                    background:
                      'linear-gradient(126deg, #6C3BB7 0%, #A689D2 28%, #7636D9 60%)',
                    backgroundBlendMode: 'overlay',
                  }}
                  onClick={() => {
                    navigate('/signup');
                  }}
                >
                  Get Started
                </button> */}
                </div>
              )}
            </div>

            {/* Image Section - Hidden on mobile view */}
            <div className="hidden lg:block mt-12 lg:mt-0 lg:ml-16 lg:max-w-3xl relative">
              <div className="relative overflow-hidden">
                <img
                  src={landingImage}
                  alt="Manufacturing"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/*Revolutionizing Manufacturing Efficiency*/}
      <AnimatedSection>
        <div className="text-center overflow-hidden bg-white px-4 py-8 lg:mt-10 lg:py-14 lg:px-12 lg:pl-16">
          <h2 className="text-4xl sm:text-[56px] leading-tight font-bold text-black">
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  'linear-gradient(330deg, #6C3BB7 0%, #9652FF 35%, #9652FF 80%)',
              }}
            >
              {' '}
              {t('home.section2.h1')}
              <br />
            </span>
            {t('home.section2.h2')}{' '}
            <span
              className=" bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  'linear-gradient(330deg, #6C3BB7 0%, #9652FF 35%, #9652FF 80%)',
              }}
            >
              {t('home.section2.h3')}
            </span>
          </h2>

          {/* Responsive Grid for Tech Cards */}
          <div className="mt-12 grid gap-8 px-4 sm:px-8 md:px-12 lg:mt-24 lg:px-16 xl:px-20 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:gap-16">
            {tech.map((data, index) => (
              <div
                key={index}
                className="flex flex-col items-center justify-between space-y-6 p-6 shadow-lg rounded-3xl border-[1px] transition-transform transform hover:scale-105"
              >
                <img className="w-16 h-16" src={data.animation} alt="Icon" />
                <span className="text-xl font-semibold text-center">
                  {data.heading}
                </span>
                <span className="text-sm text-[#637381] text-center">
                  {data.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Our Manufacturers Capabilities */}
      <AnimatedSection>
        <div className="text-center overflow-hidden bg-white px-4 py-8 lg:mt-10 lg:py-14 lg:px-12 lg:pl-16">
          <h2 className="text-4xl sm:text-[56px] leading-tight font-bold text-black">
            {t('home.section3.h1')}
          </h2>
          <p className="text-sm sm:text-base text-[#637381] mx-auto w-full lg:max-w-[860px] mt-9">
            {t('home.section3.desc')}
          </p>
        </div>
        <div className="mt-6">
          <AutoplayCarousel />
        </div>
      </AnimatedSection>

      {/* Become a MakerSharks’ Manufacturer */}
      <AnimatedSection>
        <div className="hidden lg:block relative lg:overflow-hidden lg:bg-white  lg:py-28 lg:mt-16 lg:px-12 lg:pl-16">
          <div className="hidden lg:block absolute lg:inset-x-0 lg:-bottom-0 lg:z-0">
            <img
              src="/wavyDownRegister.svg"
              alt="Wavy Background"
              className="w-full"
            />
          </div>
          <div className="z-10 lg:flex lg:flex-row lg:items-center">
            {/* Image Section - Hidden on mobile view */}
            <div className="hidden lg:block mt-12 lg:mt-0 relative">
              <div className="relative overflow-hidden">
                <img
                  src="/registerImage.svg"
                  alt="Manufacturing"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            {/* Text Section */}
            <div className="relative z-10 lg:flex lg:flex-col lg:items-start lg:ml-32 lg:max-w-[700px] lg:text-white">
              <p className="w-full font-extrabold leading-tight text-4xl lg:text-[56px]">
                {t('home.section4.h1')} <br />
                {t('home.section4.h2')}
              </p>
              <p className="mt-6 text-base">{t('home.section4.desc')}</p>
              {/* <button
              className="mt-8 px-8 py-3 text-white text-xl font-semibold rounded-md shadow-lg transition-transform transform hover:scale-105"
              style={{
                background:
                  'linear-gradient(126deg, #6C3BB7 0%, #A689D2 28%, #7636D9 60%)',
                backgroundBlendMode: 'overlay',
              }}
              onClick={scrollToForm}
            >
              {t('common.buttons.joinWaitlist')}
            </button> */}
              <CustomDemoButton
                key={'Book a Demo'}
                to={'https://cal.com/ssjaiswal'}
                type="homePage1"
                size="text-xl px-8 py-3 mt-8"
              >
                Book a demo
              </CustomDemoButton>
            </div>
          </div>
        </div>
        <div ref={formRef}></div>
      </AnimatedSection>

      {/* Contact US form */}
      <ZoomInAnimatedSection>
        <div className=" text-center mt-8 lg:text-left lg:flex lg:flex-row lg:items-center lg:justify-between lg:space-x-20 lg:overflow-hidden lg:bg-white px-4 py-8 lg:mt-10 lg:py-14 lg:px-12 lg:pl-16">
          <h2 className="text-4xl sm:text-[56px] leading-tight font-bold text-black">
            {t('home.section5.h1')} <br /> {t('home.section5.h2')} <br />{' '}
            {t('home.section5.h3')}{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  'linear-gradient(330deg, #6C3BB7 0%, #9652FF 70%, #9652FF 30%)',
              }}
            >
              {t('home.section5.h4')}
            </span>
            <p className="text-sm sm:text-base text-[#637381] mx-auto w-full font-normal lg:max-w-[860px] mt-9">
              {t('home.section5.desc')}
            </p>
          </h2>

          <div className="">
            {/* Form Section */}
            <div className="w-full mt-10 lg:mt-0 lg:min-w-[600px] lg:max-h-[530px]">
              {waitlistCondition === '' && (
                <div className="px-8 py-10 rounded-xl shadow-lg border-[1px] relative h-[600px] lg:h-[530px]">
                  <div className="grid grid-cols-2 gap-3.5">
                    <div className="relative ">
                      <input
                        type="text"
                        id="firstName"
                        name="first_name"
                        placeholder=" "
                        onFocus={() => setFocusedInput('first_name')}
                        onBlur={() => setFocusedInput('')}
                        onChange={handleInputChange}
                        value={formData.first_name}
                        className={`peer w-full border border-[#575757] border-opacity-30 p-3 ${
                          focusedInput === 'first_name'
                            ? 'pl-4 pt-4 pb-2'
                            : 'pl-10'
                        } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                      />
                      <span
                        className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 peer-focus:opacity-0`}
                      >
                        <img
                          src={firstNameIcon}
                          className="h-5 w-5 text-[#575757]"
                          alt="First Name Icon"
                        />
                      </span>
                      {(!formData.first_name ||
                        focusedInput === 'first_name') && (
                        <label
                          htmlFor="firstName"
                          className={`absolute  ${
                            focusedInput === 'first_name' ? 'left-1' : 'left-10'
                          }  top-3 text-[#575757] transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                        >
                          {t('home.form.firstName')}
                        </label>
                      )}
                      <p
                        className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                          formErrors.first_name !== ''
                            ? 'opacity-100'
                            : 'opacity-0'
                        }`}
                      >
                        {formErrors.first_name ||
                          t('validation.required.firstName')}
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        id="lastName"
                        name="last_name"
                        placeholder=" "
                        onFocus={() => setFocusedInput('last_name')}
                        onBlur={() => setFocusedInput('')}
                        onChange={handleInputChange}
                        value={formData.last_name}
                        className={`peer w-full border border-[#575757] border-opacity-30 p-3 ${
                          focusedInput === 'last_name'
                            ? 'pl-4 pt-4 pb-2'
                            : 'pl-4'
                        } rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                      />
                      {(!formData.last_name ||
                        focusedInput === 'last_name') && (
                        <label
                          htmlFor="lastName"
                          className={`absolute  ${
                            focusedInput === 'last_name' ? 'left-1' : 'left-3'
                          }  top-3 text-[#575757] transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                        >
                          {t('home.form.lastName')}
                        </label>
                      )}
                      <p
                        className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                          formErrors.last_name !== ''
                            ? 'opacity-100'
                            : 'opacity-0'
                        }`}
                      >
                        {formErrors.last_name ||
                          t('validation.required.lastName')}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 relative lg:mt-5">
                    <input
                      type="text"
                      id="companyName"
                      name="company_name"
                      placeholder=" "
                      onFocus={() => setFocusedInput('company_name')}
                      onBlur={() => setFocusedInput('')}
                      onChange={handleInputChange}
                      value={formData.company_name}
                      className={`peer w-full border border-[#575757] border-opacity-30 p-3 ${
                        focusedInput === 'company_name'
                          ? 'pl-4 pt-4 pb-2'
                          : 'pl-10'
                      } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                    />
                    <span
                      className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 ${
                        focusedInput === 'company_name'
                          ? 'opacity-0'
                          : 'opacity-100'
                      }`}
                    >
                      <img
                        src={companyIcon}
                        className="h-5 w-5 text-[#575757]"
                        alt="Company Name Icon"
                      />
                    </span>
                    {(!formData.company_name ||
                      focusedInput === 'company_name') && (
                      <label
                        htmlFor="companyName"
                        className={`absolute  ${
                          focusedInput === 'company_name' ? 'left-1' : 'left-10'
                        }  top-3 text-[#575757] transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                      >
                        {t('home.form.companyName')}
                      </label>
                    )}
                    <p
                      className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                        formErrors.company_name !== ''
                          ? 'opacity-100'
                          : 'opacity-0'
                      }`}
                    >
                      {formErrors.company_name ||
                        t('validation.required.companyName')}
                    </p>
                  </div>
                  <div className="mt-2 lg:grid lg:grid-cols-2 lg:gap-3.5 lg:mt-5">
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder=" "
                        onFocus={() => setFocusedInput('email')}
                        onBlur={() => setFocusedInput('')}
                        onChange={handleInputChange}
                        value={formData.email}
                        className={`peer w-full border border-[#575757] border-opacity-30 p-3 ${
                          focusedInput === 'email' ? 'pl-4 pt-4 pb-2' : 'pl-10'
                        } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                      />
                      <span
                        className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 ${
                          focusedInput === 'email' ? 'opacity-0' : 'opacity-100'
                        }`}
                      >
                        <img
                          src={emailIcon}
                          className="h-5 w-5 text-[#575757]"
                          alt="Email Icon"
                        />
                      </span>
                      {(!formData.email || focusedInput === 'email') && (
                        <label
                          htmlFor="email"
                          className={`absolute  ${
                            focusedInput === 'email' ? 'left-2' : 'left-10'
                          }  top-3 text-[#575757] transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                        >
                          {t('home.form.email')}
                        </label>
                      )}
                      <p
                        className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                          formErrors.email !== '' ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        {formErrors.email || t('validation.required.email')}
                      </p>
                    </div>
                    <div className="mt-2 lg:mt-0 relative">
                      <input
                        type="text"
                        id="phone_number"
                        name="phone_number"
                        placeholder=" "
                        onFocus={() => setFocusedInput('phone')}
                        onBlur={() => setFocusedInput('')}
                        onChange={handleInputChange}
                        value={formData.phone_number}
                        className={`peer w-full border border-[#575757] border-opacity-30 p-3 ${
                          focusedInput === 'phone' ? 'pl-4 pt-4 pb-2' : 'pl-10'
                        } pr-3 rounded-lg placeholder-transparent focus:outline-none focus:ring-1 focus:ring-purple-500 transition-all duration-300`}
                      />
                      <span
                        className={`absolute inset-y-0 bottom-5 left-0 flex items-center pl-3 transition-opacity duration-300 ${
                          focusedInput === 'phone' ? 'opacity-0' : 'opacity-100'
                        }`}
                      >
                        <img
                          src={phoneIcon}
                          className="h-5 w-5 text-[#575757]"
                          alt="Phone Icon"
                        />
                      </span>
                      {(!formData.phone_number || focusedInput === 'phone') && (
                        <label
                          htmlFor="phone_number"
                          className={`absolute  ${
                            focusedInput === 'phone' ? 'left-2' : 'left-10'
                          }  top-3 text-[#575757] transition-all duration-300 transform -translate-y-1/2 scale-75 hover:cursor-text peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-base peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:text-xs`}
                        >
                          {t('home.form.phoneNumber')}
                        </label>
                      )}
                      <p
                        className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                          formErrors.phone_number !== ''
                            ? 'opacity-100'
                            : 'opacity-0'
                        }`}
                      >
                        {formErrors.phone_number ||
                          t('validation.required.phoneNumber')}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 lg:mt-4">
                    <div className="flex flex-col items-start">
                      <label className="text-[#637381]">
                        {t('home.form.role')}:
                      </label>
                      <div className="flex flex-col space-y-2 lg:flex lg:flex-row justify-between mt-2 w-full">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="role"
                            value="Customer"
                            checked={formData.role === 'Customer'}
                            onChange={handleInputChange}
                            className="mr-2 accent-[#7636D9] h-6 w-6 p-2 focus:outline-none"
                          />
                          {t('home.form.buyer')}
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="role"
                            value="Manufacturer"
                            checked={formData.role === 'Manufacturer'}
                            onChange={handleInputChange}
                            className="mr-2 accent-[#7636D9] h-6 w-6 p-2 focus:outline-none"
                          />
                          {t('home.form.manufacturer')}
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="role"
                            value="Investor"
                            checked={formData.role === 'Investor'}
                            onChange={handleInputChange}
                            className="mr-2 accent-[#7636D9] h-6 w-6"
                          />
                          {t('home.form.investor')}
                        </label>
                      </div>
                      <p
                        className={`text-[#6C3BB7] text-xs mt-1 transition-all duration-300  ${
                          formErrors.role !== '' ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        {formErrors.role || 'Please select a role'}
                      </p>
                    </div>
                  </div>
                  <button
                    className={`mt-4 lg:mt-12 px-8 py-3 text-white text-xl w-full font-semibold rounded-md shadow-lg transition-all transform hover:scale-105 `}
                    style={{
                      background:
                        'linear-gradient(126deg, #6C3BB7 0%, #A689D2 28%, #7636D9 60%)',
                      backgroundBlendMode: 'overlay',
                    }}
                    onClick={handleSubmit}
                  >
                    <span className={`${processing && 'animate-fadeInOut'}`}>
                      {processing ? 'We are processing...' : 'Contact Us'}
                    </span>
                  </button>
                </div>
              )}
              {waitlistCondition === 'schedule' && (
                <div className="px-8 py-10 rounded-xl shadow-lg border-[1px] relative h-[600px] lg:h-[530px]">
                  <iframe
                    src="https://cal.com/cicada-dj-1sa4fr"
                    title="Cal.com Scheduler"
                    className="absolute top-0 right-0 w-full h-full border-0 rounded-lg"
                    allow="camera; microphone; fullscreen"
                  ></iframe>
                </div>
              )}

              {waitlistCondition === 'congrats' && (
                <div className="rounded-xl shadow-lg border-[1px] relative h-[600px] lg:h-[530px] overflow-hidden">
                  <div className="w-full h-full lg:mt-0 lg:min-w-[600px] flex items-center justify-center">
                    <div className="relative flex flex-col items-center justify-center w-full h-full">
                      <img
                        alt="WaitlistAdded"
                        src={waitlistCongratsAni}
                        className="object-cover absolute inset-y-10 right-36 w-72 h-72"
                      />
                    </div>
                  </div>
                </div>
              )}
              {waitlistCondition === 'complete' && (
                <div className="rounded-xl shadow-lg border-[1px] relative h-[600px] lg:h-[530px]">
                  <div className="px-8 py-10 w-full h-full lg:mt-0 lg:min-w-[600px]">
                    <div className="flex flex-col space-y-4 items-center justify-center h-full relative">
                      <img
                        src={waitlistCompleteAnim}
                        className="w-32 h-32"
                        alt="Completion Animation"
                      />
                      <span className="text-[#7636D9] text-[36px] font-bold text-center">
                        Thank You!{' '}
                        <p className="text-[#637381] text-[24px] font-semibold">
                          We’ve locked in your meeting, see you soon!
                        </p>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ZoomInAnimatedSection>

      {/* Footer */}
      <div>
        <Footer />
      </div>
    </div>
  );
};

export default HomePage;
